import { useEffect, useMemo, useState } from 'react';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { ForecastDayHourly, GenerationForecastDayResponse } from './forecastTypes';
import { useTheme } from '@/hooks/useTheme';

interface Props {
  assetId: string;
  hideTable?: boolean;
  showActualLine?: boolean;
}

function fmtKwh(v: number | undefined, digits = 1): string {
  if (v == null) return '—';
  return Number.isInteger(v) ? `${v}` : v.toFixed(digits);
}

function fmtInt(v: number | undefined): string {
  if (v == null) return '—';
  return Math.round(v).toString();
}

function fmtDec(v: number | undefined, digits = 1): string {
  if (v == null) return '—';
  return v.toFixed(digits);
}

function fmtDateLocal(ts: string | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtTimeHHmm(ts: string | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Kyiv' });
}

function barColor(hint: ForecastDayHourly['colorHint']): string {
  switch (hint) {
    case 'gray':
      return '#9B9BA4';
    case 'red':
      return '#D12229';
    case 'muted':
    default:
      return '#F0F0F2';
  }
}

function getKyivTodayParts() {
  const formatter = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Kyiv',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return { year: get('year'), month: get('month'), day: get('day') };
}

function shiftKyivDate(baseParts: { year: string; month: string; day: string }, offsetDays: number) {
  const base = new Date(`${baseParts.year}-${baseParts.month}-${baseParts.day}T12:00:00Z`);
  const shifted = new Date(base.getTime() + offsetDays * 86400000);
  const apiFormatter = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Kyiv',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const apiParts = apiFormatter.formatToParts(shifted);
  const get = (type: string) => apiParts.find((p) => p.type === type)?.value ?? '';
  const api = `${get('year')}-${get('month')}-${get('day')}`;
  const display = shifted.toLocaleDateString('uk-UA', {
    timeZone: 'Europe/Kyiv',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return { api, display };
}

export default function DailyHourlyForecastWidget({ assetId, hideTable = false, showActualLine = false }: Props) {
  const [dayData, setDayData] = useState<GenerationForecastDayResponse | null>(null);
  const [actualByHour, setActualByHour] = useState<Record<string, number>>({});
  const [dayLoading, setDayLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredHour, setHoveredHour] = useState<ForecastDayHourly | null>(null);
  const [showRadiationDetails, setShowRadiationDetails] = useState(false);
  const { isDark } = useTheme();

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#8E8E93' : '#6B6B70';
  const mutedText = isDark ? '#6B6B70' : '#9B9BA4';
  const kpiBg = isDark ? 'rgba(255,255,255,0.03)' : '#F9F9FB';
  const iconBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const chartBg = isDark ? 'rgba(255,255,255,0.03)' : '#FAFAFA';
  const chartBorder = isDark ? 'rgba(255,255,255,0.07)' : '#F0F0F2';
  const gridLine = isDark ? 'rgba(255,255,255,0.06)' : '#F0F0F0';
  const tabInactiveBg = isDark ? 'rgba(255,255,255,0.03)' : '#F9F9FB';
  const tabInactiveBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F9F9FB';
  const tableBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const footerBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const mutedBarColor = isDark ? 'rgba(255,255,255,0.04)' : '#F0F0F2';

  const dayTabs = useMemo(() => {
    const todayParts = getKyivTodayParts();
    return [
      { label: 'D-2', offset: -2 },
      { label: 'D-1', offset: -1 },
      { label: 'Today', offset: 0 },
      { label: 'D+1', offset: 1 },
      { label: 'D+2', offset: 2 },
    ].map((t) => {
      const { api, display } = shiftKyivDate(todayParts, t.offset);
      return { label: t.label, apiDate: api, displayDate: display };
    });
  }, []);

  const [selectedDay, setSelectedDay] = useState<string>(() => {
    const todayParts = getKyivTodayParts();
    return shiftKyivDate(todayParts, 0).api;
  });

  // Авто-оновлення: без цього віджет фетчив дані лише при монтуванні/зміні дня,
  // тож відкрита сторінка «замерзала» на годині завантаження (лаг факту по Табаках).
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setRefreshTick((t) => t + 1), 120000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!selectedDay) return;
    let cancelled = false;
    const loadDay = async () => {
      setDayLoading(true);
      setError(null);
      try {
        const result = await api.get<GenerationForecastDayResponse>(
          `/forecasting/assets/${assetId}/generation-forecasts/day?date=${selectedDay}`
        );
        if (!cancelled) setDayData(result);
      } catch (err) {
        if (cancelled) return;
        const msg = getApiErrorMessage(err, 'Failed to load daily forecast');
        if (err instanceof ApiError && err.status === 404) {
          setDayData(null);
          setError(null);
        } else {
          setError(msg);
        }
      } finally {
        if (!cancelled) setDayLoading(false);
      }
    };
    loadDay();
    return () => { cancelled = true; };
  }, [assetId, selectedDay, refreshTick]);

  // Fetch actuals for the selected day → green line overlay. Opt-in.
  // For SES without modbus data (Нікеліос, Нікополь, Лучки) the endpoint
  // returns an empty array → no line rendered, no legend shown.
  useEffect(() => {
    if (!showActualLine || !selectedDay || !assetId) {
      setActualByHour({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ hourly: Array<{ hour: string; actualPacKw: number | null }> }>(
          `/forecasting/assets/${assetId}/hourly-actuals/day?date=${selectedDay}`,
        );
        if (cancelled) return;
        const map: Record<string, number> = {};
        for (const r of res.hourly ?? []) {
          if (r.actualPacKw == null) continue;
          const val = Number(r.actualPacKw);
          if (!Number.isFinite(val)) continue;
          map[r.hour] = val;
        }
        setActualByHour(map);
      } catch {
        if (!cancelled) setActualByHour({});
      }
    })();
    return () => { cancelled = true; };
  }, [assetId, selectedDay, showActualLine, refreshTick]);

  const chart24h = useMemo(() => {
    const hourly = dayData?.hourly ?? [];
    const slots = Array.from({ length: 24 }, (_, i) => ({
      hourIndex: i,
      hourLabel: `${String(i).padStart(2, '0')}:00`,
      data: null as ForecastDayHourly | null,
    }));
    for (const h of hourly) {
      const hourNum = parseInt(h.hour.slice(0, 2), 10);
      if (Number.isFinite(hourNum) && hourNum >= 0 && hourNum < 24) {
        slots[hourNum].data = h;
      }
    }
    return slots;
  }, [dayData]);

  // Visible (daylight-only) slots for the chart.
  // Defaults: sunrise hour minus 1, sunset hour plus 1 (with buffer for ramp).
  // Falls back to 04:00..21:00 when sun data missing.
  const chartSlots = useMemo(() => {
    let startH = 4;
    let endH = 21;
    if (dayData?.sun) {
      try {
        const sr = parseInt(dayData.sun.sunrise.slice(11, 13), 10);
        const ss = parseInt(dayData.sun.sunset.slice(11, 13), 10);
        if (Number.isFinite(sr)) startH = Math.max(0, sr - 1);
        if (Number.isFinite(ss)) endH = Math.min(23, ss + 1);
      } catch { /* keep defaults */ }
    }
    return chart24h.filter((s) => s.hourIndex >= startH && s.hourIndex <= endH);
  }, [chart24h, dayData?.sun]);

  const maxForecastKwh = useMemo(() => {
    const hourly = dayData?.hourly ?? [];
    const valid = hourly.filter(
      (h) => h.status !== 'missing' && h.forecastEnergyKwh != null && h.colorHint !== 'muted',
    );
    if (valid.length === 0) return 0;
    return Math.max(...valid.map((h) => Number(h.forecastEnergyKwh)));
  }, [dayData]);

  const yAxisMax = useMemo(() => {
    if (maxForecastKwh <= 0) return 100;
    const withHeadroom = maxForecastKwh * 1.12;
    return Math.ceil(withHeadroom / 100) * 100;
  }, [maxForecastKwh]);

  const summary = dayData?.summary;

  return (
    <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      {/* Header with day tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(209,34,41,0.08)' }}>
            <i className="ri-time-line text-sm" style={{ color: '#D12229' }}></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: textMain }}>Daily Hourly Forecast</h3>
            <p className="text-xs" style={{ color: textMuted }}>{(dayData?.asset as any)?.name ?? '—'} · Hourly Sale / Export Forecast</p>
          </div>
        </div>

        <div className="flex items-center gap-1 md:gap-1.5 flex-wrap">
          {dayTabs.map((tab) => (
            <button
              key={tab.apiDate}
              onClick={() => setSelectedDay(tab.apiDate)}
              className="px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-[11px] md:text-xs font-medium cursor-pointer transition-colors whitespace-nowrap"
              style={{
                background: selectedDay === tab.apiDate ? '#D12229' : tabInactiveBg,
                color: selectedDay === tab.apiDate ? '#fff' : textMuted,
                border: selectedDay === tab.apiDate ? '1px solid #D12229' : `1px solid ${tabInactiveBorder}`,
              }}
            >
              <span className="block">{tab.label}</span>
              <span className="block text-[9px] md:text-[10px] opacity-80">{tab.displayDate}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-6 text-center mb-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}>
            <i className="ri-error-warning-line text-xl" style={{ color: '#D12229' }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: textMain }}>Failed to load forecast</p>
          <p className="text-xs mt-1" style={{ color: textMuted }}>{error}</p>
        </div>
      )}

      {/* Empty state */}
      {!error && !dayLoading && !dayData && (
        <div className="p-6 text-center mb-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}>
            <i className="ri-time-line text-xl" style={{ color: textMuted }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: textMain }}>No hourly forecast data available</p>
        </div>
      )}

      {/* Day loading */}
      {dayLoading && (
        <div className="mb-4 text-center">
          <i className="ri-loader-4-line animate-spin text-lg" style={{ color: textMuted }}></i>
          <p className="text-xs mt-1" style={{ color: textMuted }}>Loading day details...</p>
        </div>
      )}

      {!error && !dayLoading && dayData && (
        <>
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
              <div className="p-3 rounded-lg" style={{ background: kpiBg }}>
                <div className="text-xs mb-1" style={{ color: textMuted }}>Forecast kWh</div>
                <div className="text-base font-bold" style={{ color: textMain }}>{fmtKwh(summary.forecastEnergyKwh)}</div>
              </div>
              <div className="p-3 rounded-lg" style={{ background: kpiBg }}>
                <div className="text-xs mb-1" style={{ color: textMuted }}>Max hourly kWh</div>
                <div className="text-base font-bold" style={{ color: textMain }}>{fmtKwh(summary.maxHourlyKwh)}</div>
              </div>
              <div className="p-3 rounded-lg" style={{ background: kpiBg }}>
                <div className="text-xs mb-1" style={{ color: textMuted }}>Avg cloud cover</div>
                <div className="text-base font-bold" style={{ color: textMain }}>
                  {summary.avgCloudCover != null ? `${summary.avgCloudCover.toFixed(1)}%` : '—'}
                </div>
              </div>
              <div className="p-3 rounded-lg" style={{ background: kpiBg }}>
                <div className="text-xs mb-1" style={{ color: textMuted }}>Max shortwave</div>
                <div className="text-base font-bold" style={{ color: textMain }}>
                  {summary.maxShortwaveRadiation != null ? `${Math.round(summary.maxShortwaveRadiation)} W/m²` : '—'}
                </div>
              </div>
              <div className="p-3 rounded-lg" style={{ background: kpiBg }}>
                <div className="text-xs mb-1" style={{ color: textMuted }}>Temp min–max</div>
                <div className="text-base font-bold" style={{ color: textMain }}>
                  {summary.minTemperature2m != null && summary.maxTemperature2m != null
                    ? `${summary.minTemperature2m.toFixed(1)} — ${summary.maxTemperature2m.toFixed(1)} °C`
                    : '—'}
                </div>
              </div>
            </div>
          )}

          {dayData?.sun && (
            <div className="mb-3 text-xs flex items-center gap-1.5" style={{ color: textMuted }}>
              <i className="ri-sun-line text-xs" style={{ color: '#D12229' }}></i>
              <span className="font-medium">Sunrise:</span> {fmtTimeHHmm(dayData.sun.sunrise)}
              <span className="mx-1" style={{ color: isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8' }}>·</span>
              <i className="ri-moon-line text-xs" style={{ color: mutedText }}></i>
              <span className="font-medium">Sunset:</span> {fmtTimeHHmm(dayData.sun.sunset)}
            </div>
          )}

          {dayData && dayData.operationalCapacityRatio !== undefined && dayData.operationalCapacityRatio < 0.98 && (
            <div className="mb-3 text-xs flex items-center gap-2 px-3 py-1.5 rounded-md inline-flex"
                 style={{ color: '#C2410C', background: isDark ? 'rgba(217,119,6,0.10)' : '#FFEDD5', border: `1px solid ${isDark ? 'rgba(217,119,6,0.25)' : '#FED7AA'}` }}>
              <i className="ri-tools-line text-sm"></i>
              <span><strong>{(dayData.operationalCapacityMw ?? 0).toFixed(3)} МВт</strong> з {(dayData.installedCapacityMw ?? 0).toFixed(3)} МВт спец ({((dayData.operationalCapacityRatio ?? 1) * 100).toFixed(0)}%) — частина обладнання не працює</span>
              {dayData.operationalCapacitySource && (
                <span className="opacity-70">· {dayData.operationalCapacitySource === 'manual_dispatcher' ? 'диспетчер' : dayData.operationalCapacitySource === 'auto_modbus_estimate' ? 'авто' : 'спец'}</span>
              )}
            </div>
          )}

          {summary && summary.missingRows != null && summary.missingRows > 0 && (
            <div className="mb-3 text-xs" style={{ color: mutedText }}>
              Some forecast hours are not available because forecast history starts after timezone correction.
            </div>
          )}

          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => setShowRadiationDetails((v) => !v)}
              className="flex items-center gap-2 px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer transition-colors"
              style={{
                background: showRadiationDetails ? 'rgba(209,34,41,0.08)' : kpiBg,
                color: showRadiationDetails ? '#D12229' : textMuted,
                border: `1px solid ${tabInactiveBorder}`,
              }}
            >
              <i className={`${showRadiationDetails ? 'ri-eye-line' : 'ri-eye-off-line'} text-xs`}></i>
              Show solar radiation details
            </button>
          </div>

          {/* Hourly chart */}
          <div className="mb-5">
            <div className="relative rounded-lg" style={{ background: chartBg, border: `1px solid ${chartBorder}` }}>
              <div className="relative" style={{ height: '340px' }}>
                <div
                  className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-[10px] text-right"
                  style={{ width: '44px', color: textMuted, padding: '16px 4px 32px 8px' }}
                >
                  <span>{fmtInt(yAxisMax)}</span>
                  <span>{fmtInt(Math.round(yAxisMax * 0.75))}</span>
                  <span>{fmtInt(Math.round(yAxisMax * 0.5))}</span>
                  <span>{fmtInt(Math.round(yAxisMax * 0.25))}</span>
                  <span>0</span>
                </div>

                <div className="absolute" style={{ left: '44px', right: '0', top: '0', bottom: '0' }}>
                  <div className="absolute" style={{ top: '16px', right: '12px', bottom: '32px', left: '0' }}>
                    <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <div key={i} style={{ borderTop: `1px solid ${gridLine}` }} />
                      ))}
                    </div>

                    {(() => {
                      // OSR/SKO cap: draw a horizontal dashed line at the cap level.
                      // Useful visual reference for clamped sites like Bolgrad today.
                      const cap = (dayData as any)?.activeCurtailment?.curtailmentPmaxKw;
                      if (cap == null || yAxisMax <= 0) return null;
                      const capVal = Number(cap);
                      if (capVal <= 0 || capVal >= yAxisMax) return null;
                      const topPct = (1 - capVal / yAxisMax) * 100;
                      return (
                        <div
                          className="absolute left-0 right-0 pointer-events-none z-0"
                          style={{ top: `${topPct.toFixed(1)}%`, borderTop: '1px dashed #F59E0B' }}
                        >
                          <span className="absolute -top-3 right-1 text-[9px] px-1 rounded"
                                style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B' }}>
                            OSR cap {capVal.toFixed(0)} kW
                          </span>
                        </div>
                      );
                    })()}

                    {dayData?.sun && (
                      <>
                        {(() => {
                          const sunriseHour = parseInt(dayData.sun.sunrise.slice(11, 13), 10);
                          const sunsetHour = parseInt(dayData.sun.sunset.slice(11, 13), 10);
                          // Map hour to position WITHIN the visible window.
                          const visStart = chartSlots[0]?.hourIndex ?? 0;
                          const visLen = Math.max(chartSlots.length, 1);
                          const leftPct = (i: number) => `${((i - visStart) / visLen) * 100}%`;
                          const slotWidthPct = (1 / visLen) * 100;
                          return (
                            <>
                              <div
                                className="absolute top-0 bottom-8 pointer-events-none z-0"
                                style={{ left: `calc(${leftPct(sunriseHour)} + ${slotWidthPct / 2}%)`, borderLeft: '1px dashed rgba(209,34,41,0.25)' }}
                              >
                                <span className="absolute -top-0 left-1 text-[9px] whitespace-nowrap" style={{ color: 'rgba(209,34,41,0.6)' }}>
                                  {fmtTimeHHmm(dayData.sun.sunrise)}
                                </span>
                              </div>
                              <div
                                className="absolute top-0 bottom-8 pointer-events-none z-0"
                                style={{ left: `calc(${leftPct(sunsetHour)} + ${slotWidthPct / 2}%)`, borderLeft: '1px dashed rgba(155,155,164,0.35)' }}
                              >
                                <span className="absolute -top-0 left-1 text-[9px] whitespace-nowrap" style={{ color: 'rgba(155,155,164,0.7)' }}>
                                  {fmtTimeHHmm(dayData.sun.sunset)}
                                </span>
                              </div>
                            </>
                          );
                        })()}
                      </>
                    )}

                    <div
                      className="absolute inset-0 pointer-events-none z-20"
                      style={{ overflow: 'hidden' }}
                    >
                      {showActualLine && (() => {
                        type Pt = { x: number; y: number; v: number; label: string };
                        const pts: Pt[] = [];
                        chartSlots.forEach((slot, i) => {
                          const v = actualByHour[slot.hourLabel];
                          if (v == null || !Number.isFinite(v)) return;
                          const x = ((i + 0.5) / Math.max(chartSlots.length, 1)) * 100;
                          const yRaw = 100 - (v / Math.max(yAxisMax, 1)) * 100;
                          const y = Math.max(0, Math.min(100, yRaw));
                          pts.push({ x, y, v, label: slot.hourLabel });
                        });
                        if (pts.length === 0) return null;

                        // Sloped step (trapezoidal): hold the value flat for most of
                        // the slot, then ramp up/down with a short slope to the next
                        // point's level. Avoids the harsh right-angle look of pure step.
                        const buildStepPath = (points: Pt[]): string => {
                          if (points.length === 0) return '';
                          let d = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
                          for (let i = 1; i < points.length; i++) {
                            const prev = points[i - 1];
                            const cur = points[i];
                            const slotW = cur.x - prev.x;
                            // 25% of slot width used as the sloped transition zone
                            const rampX = prev.x + slotW * 0.75;
                            d += ` L ${rampX.toFixed(2)} ${prev.y.toFixed(2)}`;
                            d += ` L ${cur.x.toFixed(2)} ${cur.y.toFixed(2)}`;
                          }
                          return d;
                        };

                        return (
                          <svg
                            width="100%"
                            height="100%"
                            viewBox="0 0 100 100"
                            preserveAspectRatio="none"
                            overflow="hidden"
                            style={{ display: 'block' }}
                          >
                            {/* Operational forecast — orange step line (shown only when meaningfully below spec) */}
                            {(() => {
                              const ratio = dayData?.operationalCapacityRatio;
                              if (ratio === undefined || ratio >= 0.98) return null;
                              const opPts = chartSlots.map((slot, i) => {
                                const h = slot.data;
                                if (!h || h.forecastEnergyKwh == null) return null;
                                const v = Number(h.forecastEnergyKwh) * ratio;
                                const x = ((i + 0.5) / Math.max(chartSlots.length, 1)) * 100;
                                const y = Math.max(0, Math.min(100, 100 - (v / Math.max(yAxisMax, 1)) * 100));
                                return { x, y };
                              }).filter(Boolean) as { x: number; y: number }[];
                              if (opPts.length === 0) return null;
                              let d = `M ${opPts[0].x.toFixed(2)} ${opPts[0].y.toFixed(2)}`;
                              for (let i = 1; i < opPts.length; i++) {
                                d += ` H ${opPts[i].x.toFixed(2)} V ${opPts[i].y.toFixed(2)}`;
                              }
                              return (
                                <path
                                  d={d}
                                  fill="none"
                                  stroke="#C2410C"
                                  strokeWidth={1.75}
                                  strokeDasharray="4 3"
                                  strokeLinecap="butt"
                                  strokeLinejoin="miter"
                                  vectorEffect="non-scaling-stroke"
                                  opacity={0.85}
                                />
                              );
                            })()}
                            <path
                              d={buildStepPath(pts)}
                              fill="none"
                              stroke="#16A34A"
                              strokeWidth={2}
                              strokeLinecap="butt"
                              strokeLinejoin="miter"
                              vectorEffect="non-scaling-stroke"
                            />
                          </svg>
                        );
                      })()}
                    </div>

                    <div className="flex items-end gap-[2px] h-full relative z-10">
                      {chartSlots.map((slot, visIdx) => {
                        const h = slot.data;
                        const isMissing = !h || h.status === 'missing' || h.forecastEnergyKwh == null || h.colorHint === 'muted';
                        const value = isMissing ? 0 : Number(h.forecastEnergyKwh ?? 0);
                        const color = isMissing ? mutedBarColor : barColor(h.colorHint);
                        const pct = !isMissing && value > 0 && yAxisMax > 0 ? Math.max(2, (value / yAxisMax) * 100) : 0;
                        const barHeight = pct > 0 ? `${pct}%` : '1px';
                        const showTooltipBelow = pct > 85;

                        return (
                          <div
                            key={slot.hourIndex}
                            className="flex-1 min-w-0 h-full flex flex-col items-center justify-end group relative"
                            onMouseEnter={() => h && setHoveredHour(h)}
                            onMouseLeave={() => setHoveredHour(null)}
                          >
                            {h && hoveredHour?.hour === h.hour && (
                              <div
                                className={`absolute z-50 rounded-lg px-4 py-3 text-xs shadow-xl transition-opacity duration-150 ${visIdx <= 1 ? 'left-0' : visIdx >= chartSlots.length - 2 ? 'right-0' : 'left-1/2 -translate-x-1/2'} ${showTooltipBelow ? 'top-full mt-2' : 'bottom-full mb-2'}`}
                                style={{
                                  background: '#242426',
                                  color: '#fff',
                                  border: '1px solid rgba(255,255,255,0.06)',
                                  minWidth: 210,
                                  boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
                                  pointerEvents: 'none',
                                }}
                              >
                                <div
                                  className={`absolute ${visIdx <= 1 ? 'left-3' : visIdx >= chartSlots.length - 2 ? 'right-3' : 'left-1/2 -translate-x-1/2'} ${showTooltipBelow ? 'top-[-5px]' : 'bottom-[-5px]'}`}
                                  style={{
                                    width: 0, height: 0,
                                    borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
                                    ...(showTooltipBelow ? { borderBottom: '5px solid #242426' } : { borderTop: '5px solid #242426' }),
                                  }}
                                />
                                <div className="font-semibold mb-1.5 text-[13px]">{h.hour ?? '—'}</div>
                                <div className="space-y-1">
                                  <div className="flex justify-between gap-4">
                                    <span style={{ color: '#9B9BA4' }}>Forecast</span>
                                    <span className="font-medium">{fmtKwh(h.forecastEnergyKwh)} kWh</span>
                                  </div>
                                  {(() => {
                                    if (!showActualLine) return null;
                                    const av = actualByHour[h.hour];
                                    if (av == null) return null;
                                    return (
                                      <div className="flex justify-between gap-4">
                                        <span style={{ color: '#9B9BA4' }}>Actual</span>
                                        <span className="font-medium" style={{ color: '#16A34A' }}>{fmtKwh(av)} kWh</span>
                                      </div>
                                    );
                                  })()}
                                  {(() => {
                                    const ratio = dayData?.operationalCapacityRatio;
                                    if (ratio === undefined || ratio >= 0.98) return null;
                                    if (h.forecastEnergyKwh == null) return null;
                                    const op = Number(h.forecastEnergyKwh) * ratio;
                                    return (
                                      <div className="flex justify-between gap-4">
                                        <span style={{ color: '#9B9BA4' }}>Operational</span>
                                        <span className="font-medium" style={{ color: '#C2410C' }}>
                                          {fmtKwh(op)} kWh
                                        </span>
                                      </div>
                                    );
                                  })()}
                                  {(() => {
                                    const cap = (dayData as any)?.activeCurtailment?.curtailmentPmaxKw;
                                    if (cap == null) return null;
                                    return (
                                      <div className="flex justify-between gap-4">
                                        <span style={{ color: '#9B9BA4' }}>OSR cap</span>
                                        <span className="font-medium" style={{ color: '#F59E0B' }}>
                                          ≤ {fmtKwh(Number(cap))} kWh
                                        </span>
                                      </div>
                                    );
                                  })()}
                                  <div className="flex justify-between gap-4">
                                    <span style={{ color: '#9B9BA4' }}>Shortwave</span>
                                    <span className="font-medium">{fmtInt(h.weather?.shortwaveRadiation)} W/m²</span>
                                  </div>
                                  <div className="flex justify-between gap-4">
                                    <span style={{ color: '#9B9BA4' }}>Direct</span>
                                    <span className="font-medium">{fmtInt(h.weather?.directRadiation)} W/m²</span>
                                  </div>
                                  <div className="flex justify-between gap-4">
                                    <span style={{ color: '#9B9BA4' }}>Diffuse</span>
                                    <span className="font-medium">{fmtInt(h.weather?.diffuseRadiation)} W/m²</span>
                                  </div>
                                  <div className="flex justify-between gap-4">
                                    <span style={{ color: '#9B9BA4' }}>DNI</span>
                                    <span className="font-medium">{fmtInt(h.weather?.directNormalIrradiance)} W/m²</span>
                                  </div>
                                  <div className="flex justify-between gap-4">
                                    <span style={{ color: '#9B9BA4' }}>Cloud</span>
                                    <span className="font-medium">{fmtDec(h.weather?.cloudCover, 1)}%</span>
                                  </div>
                                  {(h.weather?.cloudCoverLow != null || h.weather?.cloudCoverMid != null || h.weather?.cloudCoverHigh != null) && (
                                    <div className="flex justify-between gap-4">
                                      <span style={{ color: '#9B9BA4' }}>Cloud L/M/H</span>
                                      <span className="font-medium">
                                        {fmtDec(h.weather?.cloudCoverLow, 0)}{' / '}{fmtDec(h.weather?.cloudCoverMid, 0)}{' / '}{fmtDec(h.weather?.cloudCoverHigh, 0)}%
                                      </span>
                                    </div>
                                  )}
                                  <div className="flex justify-between gap-4">
                                    <span style={{ color: '#9B9BA4' }}>Temp</span>
                                    <span className="font-medium">{fmtDec(h.weather?.temperature2m, 1)}°C</span>
                                  </div>
                                  {h.weather?.relativeHumidity2m != null && (
                                    <div className="flex justify-between gap-4">
                                      <span style={{ color: '#9B9BA4' }}>Humidity</span>
                                      <span className="font-medium">{fmtDec(h.weather?.relativeHumidity2m, 0)}%</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            <div
                              className="transition-all duration-200"
                              style={{
                                width: '85%',
                                height: barHeight,
                                background: color,
                                opacity: isMissing ? 1 : 0.55,
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>

                    <div className="absolute pointer-events-none z-30 flex items-center gap-2" style={{ top: '4px', right: '8px' }}>
                      {dayData?.operationalCapacityRatio !== undefined && dayData.operationalCapacityRatio < 0.98 && (
                        <div className="text-[10px] flex items-center gap-1.5 px-2 py-0.5 rounded-md"
                             style={{ background: 'rgba(194,65,12,0.10)', color: '#C2410C' }}>
                          <span style={{ display: 'inline-block', width: 16, height: 0, borderTop: '2px dashed #C2410C' }}></span>
                          <span>прогноз з врахуванням стану</span>
                        </div>
                      )}
                      {showActualLine && Object.keys(actualByHour).length > 0 && (
                        <div className="text-[10px] flex items-center gap-1.5 px-2 py-0.5 rounded-md"
                             style={{ background: 'rgba(34,197,94,0.10)', color: '#16A34A' }}>
                          <span style={{ display: 'inline-block', width: 16, height: 0, borderTop: '2px solid #16A34A' }}></span>
                          <span>факт</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="absolute left-0 flex items-center" style={{ bottom: '0', right: '12px', height: '32px' }}>
                    {chartSlots.map((slot) => (
                      <div key={`label-${slot.hourIndex}`} className="flex-1 min-w-0 text-center text-[10px]" style={{ color: textMuted }}>
                        {slot.hourLabel}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {!hideTable && <div className="overflow-x-auto" style={{ maxHeight: 300 }}>
            <table className="w-full text-sm" style={{ minWidth: showRadiationDetails ? 720 : 560 }}>
              <thead className="sticky top-0">
                <tr style={{ background: tableHeaderBg }}>
                  <th className="text-left px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Time</th>
                  <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Forecast kWh</th>
                  <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Shortwave W/m²</th>
                  {showRadiationDetails && (
                    <>
                      <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Direct W/m²</th>
                      <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Diffuse W/m²</th>
                      <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>DNI W/m²</th>
                    </>
                  )}
                  <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Cloud %</th>
                  <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Temp °C</th>
                  <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Wind km/h</th>
                  <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Precip mm</th>
                </tr>
              </thead>
              <tbody>
                {chart24h.map((slot) => {
                  const h = slot.data;
                  const isMissingRow = !h || h.status === 'missing' || h.forecastEnergyKwh == null || h.colorHint === 'muted';
                  const mainColor = isMissingRow ? mutedText : textMain;
                  const secondaryColor = isMissingRow ? mutedText : textMuted;
                  return (
                    <tr key={slot.hourIndex} style={{ borderBottom: `1px solid ${tableBorder}` }}>
                      <td className="px-3 py-2 whitespace-nowrap" style={{ color: mainColor }}>{h?.hour ?? slot.hourLabel}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap font-medium" style={{ color: mainColor }}>{isMissingRow ? '—' : fmtKwh(h.forecastEnergyKwh)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap" style={{ color: secondaryColor }}>{isMissingRow ? '—' : fmtInt(h.weather?.shortwaveRadiation)}</td>
                      {showRadiationDetails && (
                        <>
                          <td className="px-3 py-2 text-right whitespace-nowrap" style={{ color: secondaryColor }}>{isMissingRow ? '—' : fmtInt(h.weather?.directRadiation)}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap" style={{ color: secondaryColor }}>{isMissingRow ? '—' : fmtInt(h.weather?.diffuseRadiation)}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap" style={{ color: secondaryColor }}>{isMissingRow ? '—' : fmtInt(h.weather?.directNormalIrradiance)}</td>
                        </>
                      )}
                      <td className="px-3 py-2 text-right whitespace-nowrap" style={{ color: secondaryColor }}>{isMissingRow ? '—' : fmtDec(h.weather?.cloudCover, 1)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap" style={{ color: secondaryColor }}>{isMissingRow ? '—' : fmtDec(h.weather?.temperature2m, 1)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap" style={{ color: secondaryColor }}>{isMissingRow ? '—' : fmtDec(h.weather?.windSpeed10m, 1)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap" style={{ color: secondaryColor }}>{isMissingRow ? '—' : fmtDec(h.weather?.precipitation, 1)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>}
        </>
      )}

      <div className="mt-4 pt-3 text-xs flex items-center gap-1.5 flex-wrap" style={{ color: textMuted, borderTop: `1px solid ${footerBorder}` }}>
        {(() => {
          const algoRev = dayData?.algorithmRevision ?? dayData?.hourly?.find((h) => (h as any)?.algorithmRevision)?.algorithmRevision ?? null;
          return (
            <>
              <span className="font-medium">Model:</span> {dayData?.modelVersion ?? '—'}
              {algoRev ? <span style={{ color: mutedText }}> (algo {algoRev})</span> : null} ·
            </>
          );
        })()}
        <span className="font-medium">Weather:</span> {dayData?.inputWeatherProvider ?? '—'} ·
        <span className="font-medium">Target:</span> {dayData?.targetType ?? '—'} ·
        <span className="font-medium">Weather input:</span> {fmtDateLocal(dayData?.inputWeatherRunAt)}
      </div>
    </div>
  );
}